import { Dispatch } from "redux";
import { ID, Condition } from "../../../types";
import { addChoice, removeChoice } from "../../actions/choiceActions";
import {
  addDisabled,
  addMultipleDisabled,
  removeDisabled,
  removeMultipleDisabled,
} from "../../actions/disabledActions";
import { addHidden, removeHidden } from "../../actions/hiddenActions";
import { addPackaged, removePackaged } from "../../actions/packagedActions";
import { RootState } from "../../reducers";

type ConditionEffectRouteFn = (
  method: "add" | "remove"
) => (condition: Condition, state: RootState, dispatch: Dispatch) => void;

const idField = (field: ID) => (isNaN(Number(field)) ? field : Number(field));

const conditionHiddenRoute: ConditionEffectRouteFn =
  (method) => (condition, state, dispatch) => {
    const otherId = idField(condition.field);
    if (method === "add") {
      if (state.choices.allIds.includes(otherId)) {
        dispatch(removeChoice(otherId));
      }
      dispatch(addHidden(otherId));
    } else {
      dispatch(removeHidden(otherId));
    }
  };

const conditionShownRoute: ConditionEffectRouteFn =
  (method) => (condition, state, dispatch) => {
    const otherId = idField(condition.field);
    if (method === "add") {
      dispatch(removeHidden(otherId));
    } else {
      dispatch(removeChoice(otherId));
      dispatch(addHidden(otherId));
    }
  };

const conditionDisabledRoute: ConditionEffectRouteFn =
  (method) => (condition, state, dispatch) => {
    const otherId = idField(condition.field);
    if (method === "add") {
      if (state.choices.allIds.includes(otherId)) {
        dispatch(removeChoice(otherId));
      }
      dispatch(addDisabled(otherId));
    } else {
      dispatch(removeDisabled(otherId));
    }
  };

const conditionEnabledRoute: ConditionEffectRouteFn =
  (method) => (condition, state, dispatch) => {
    const otherId = idField(condition.field);
    if (method === "add") {
      dispatch(removeDisabled(otherId));
    } else {
      if (state.choices.allIds.includes(otherId)) {
        dispatch(removeChoice(otherId));
      }
      dispatch(addDisabled(otherId));
    }
  };

const conditionSelectedRoute: ConditionEffectRouteFn =
  (method) => (condition, state, dispatch) => {
    const otherId = idField(condition.field);
    if (method === "add") {
      const otherPart = state.formData.entities.parts.byId[otherId];
      if (!otherPart) return;
      dispatch(addChoice(otherPart));
    } else {
      dispatch(removeChoice(otherId));
      dispatch(removeDisabled(otherId));
    }
  };

const conditionPackagedRoute: ConditionEffectRouteFn =
  (method) => (condition, state, dispatch) => {
    const otherId = idField(condition.field);
    const otherPart = state.formData.entities.parts.byId[otherId];
    if (!otherPart) return;

    const otherPartSection =
      state.formData.entities.sections.byId[otherPart.sectionId];
    if (!otherPartSection) return;

    const sectionType = otherPartSection.type;

    if (sectionType === "single") {
      if (method === "add") {
        dispatch(addMultipleDisabled(otherPartSection.parts));
      } else {
        dispatch(removeMultipleDisabled(otherPartSection.parts));
      }
    } else if (sectionType === "group") {
      const otherPartPage =
        state.formData.entities.pages.byId[otherPart.pageId];
      if (!otherPartPage) return;
      otherPartPage.sections.forEach((sectionId) => {
        const otherPartSectionSibling =
          state.formData.entities.sections.byId[sectionId];
        if (
          !otherPartSectionSibling ||
          otherPartSectionSibling.type !== "group"
        )
          return;
        if (method === "add") {
          dispatch(addMultipleDisabled(otherPartSectionSibling.parts));
        } else {
          dispatch(removeMultipleDisabled(otherPartSectionSibling.parts));
        }
      });
    }

    if (method === "add") {
      dispatch(addDisabled(otherId));
      dispatch(addChoice(otherPart));
      dispatch(addPackaged(otherId, condition.partId));
    } else {
      dispatch(removeChoice(otherId));
      dispatch(removeDisabled(otherId));
      dispatch(removePackaged(otherId));
    }
  };

const conditionsEffectRouter = (method: "add" | "remove") => ({
  hidden: conditionHiddenRoute(method),
  shown: conditionShownRoute(method),
  disabled: conditionDisabledRoute(method),
  enabled: conditionEnabledRoute(method),
  selected: conditionSelectedRoute(method),
  packaged: conditionPackagedRoute(method),
});

export default conditionsEffectRouter;
